import { type CSSProperties, useMemo } from "react";
import { StoreApi, UseBoundStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import {
  FieldDefinitionFieldType,
  FieldDelta,
  IObjectWithTypename,
  NodeStore,
} from "@/components/@types";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import {
  isRequiredFieldMissing,
  useNodeField,
} from "@/components/organisms/Node/Node.hooks";
import {
  createBeforeData,
  formatDeltaValue,
} from "@/components/organisms/Node/Node.delta";
import { NodeLabelCell } from "@/components/organisms/Node/NodeLabelCell";
import { NodeValueCell } from "@/components/organisms/Node/NodeValueCell";
import {
  DEFAULT_DELTA_APPEARANCE,
  DeltaAppearance,
} from "@/components/organisms/Table/Table.tools";
import { CellProps } from "@/hooks/useContextTree";
import { useSizing } from "@/hooks/useSizing";
import { Values } from "@/constants";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function NodeCellLayout({
  store,
  fieldDefinition,
  isLoading,
  isSingleClick,
  size,
  getCellProps,
  rowIndex,
  isMultiline = true,
  deltaMode = false,
  deltaAppearance,
  isGridStyle = false,
  isRowDimmed = false,
  changeVariant,
  fieldVariant = "node",
}: {
  store: UseBoundStore<StoreApi<NodeStore>>;
  fieldDefinition: ObjectFieldDefinitionType<IObjectWithTypename>;
  isLoading: boolean;
  isSingleClick: boolean;
  size: FieldDefinitionFieldType<
    ObjectFieldDefinitionType<IObjectWithTypename>
  >["size"];

  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  rowIndex: number;
  isMultiline?: boolean;
  /** Delta mode: render label, value before, value after as 3 columns */
  deltaMode?: boolean;
  /** Background colors for diff (delta) cells. */
  deltaAppearance?: Required<DeltaAppearance>;
  /** When true, use compact table-like cell styling (like grid viewMode) */
  isGridStyle?: boolean;
  /** When true, dim only the content (not cell background) for non-diffed rows; row hover clears dim */
  isRowDimmed?: boolean;
  /** Hint about the kind of change: "create" → all-green bars, empty before column */
  changeVariant?: "create" | "update";
  fieldVariant?: FieldDefinitionFieldType<
    ObjectFieldDefinitionType<IObjectWithTypename>
  >["fieldVariant"];
}) {
  const cellBackground =
    deltaAppearance?.cellBackground ?? DEFAULT_DELTA_APPEARANCE.cellBackground;
  const rowHeight =
    size === "md" ? Values.DEFAULT_ROW_HEIGHT : Values.COMPACT_ROW_HEIGHT;

  const { fieldValues, fieldEvents } = useNodeField(
    store,
    fieldDefinition.objectKey,
  );
  const { cellStyles } = useSizing(size);
  const isEditable = fieldDefinition.isEditable !== false;

  // Get all field definitions from store to build inputKey -> objectKey mapping
  const allFieldDefinitions = store((state) => state.fieldDefinitions);

  // Build mapping from backend inputKey to frontend objectKey
  const inputKeyToObjectKey = useMemo(() => {
    const mapping: Record<string, string> = {};
    for (const fd of allFieldDefinitions) {
      if (fd.inputKey && fd.inputKey !== fd.objectKey) {
        mapping[fd.inputKey] = fd.objectKey;
      }
    }
    return mapping;
  }, [allFieldDefinitions]);

  // Get pending change deltas from store
  const pendingChange = store(
    useShallow((state) => state.fieldValues?.pendingChange),
  ) as { deltas?: FieldDelta[] } | null | undefined;

  // Find delta for this specific field - check both objectKey and inputKey
  const fieldDelta = useMemo(() => {
    if (!pendingChange?.deltas) return null;
    const inputKey = fieldDefinition.inputKey;
    return (
      pendingChange.deltas.find(
        (d) =>
          d.fieldName === fieldDefinition.objectKey ||
          (inputKey && d.fieldName === inputKey),
      ) ?? null
    );
  }, [
    pendingChange?.deltas,
    fieldDefinition.objectKey,
    fieldDefinition.inputKey,
  ]);

  // For "create" nodes every field is considered new (hasDelta = true)
  const hasDelta = changeVariant === "create" ? true : !!fieldDelta;

  const singleClickTypes = ["boolean", "button", "pictures", "select"];
  const effectiveSingleClick =
    isSingleClick || singleClickTypes.includes(fieldDefinition.type);

  // What a create form needs is marked there; an edit form shows no asterisks.
  const isCreating = store((state) => state.mode === "create");
  const isMissingRequired =
    isCreating &&
    isRequiredFieldMissing(
      fieldDefinition,
      fieldValues as Record<string, unknown>,
    );
  const isRevealingMissingRequired = store(
    (state) => state.missingRequiredRevealCount > 0,
  );
  const cellProps = {
    ...getCellProps({
      rowIndex,
      colIndex: 0,
      objectKey: fieldDefinition.objectKey,
    }),
    // Where "Create" puts the cursor when this is the first field it still needs.
    ...(isMissingRequired && { "data-missing-required": "true" }),
  } as CellProps;

  // Table fields render full-width in delta mode because before/after
  // comparison doesn't work well for nested tabular data in narrow columns.
  const isEffectivelyFullWidth =
    fieldDefinition.isFullWidth ||
    (deltaMode && fieldDefinition.type === "table");

  if (isEffectivelyFullWidth) {
    const columnSpan = deltaMode ? 4 : 2;
    const isTable = fieldDefinition.type === "table";
    const isTableInDelta = isTable && deltaMode;

    return (
      <div
        className={cn(
          columnSpan === 4 ? "col-[span_4]" : "col-[span_2]",
          "max-w-full w-full h-full",
          isTableInDelta ? "p-0 bg-(--cell-background)" : "p-[2px] bg-card",
        )}
        style={{ "--cell-background": cellBackground } as CSSProperties}
      >
        {isTableInDelta && (
          <div className="pl-2 pr-2 pt-1 pb-1">
            <Typography
              level="body-xs"
              className="[font-weight:600] text-muted-foreground"
            >
              {fieldDefinition.label}
            </Typography>
          </div>
        )}
        <FieldCell
          type={fieldDefinition.type}
          getValue={fieldDefinition.getValue}
          getValueOptions={fieldDefinition?.getValueOptions}
          data={fieldValues}
          isEditable={isEditable}
          store={store}
          fieldEvents={fieldEvents}
          isLoading={isLoading}
          isMultiline={isMultiline}
          isSingleClick={effectiveSingleClick}
          fieldVariant={fieldVariant}
          hoverBackgroundColor={isEditable ? undefined : null}
          slotProps={{
            cell: cellProps,
          }}
        />
      </div>
    );
  }

  // Build tooltip content for delta (only used when NOT in deltaMode)
  const deltaTooltip = fieldDelta ? (
    <div className="p-1">
      <Typography level="body-xs" className="mb-1">
        Pending change
      </Typography>
      <Typography level="body-xs">
        <strong>Was:</strong> {formatDeltaValue(fieldDelta.originalValue)}
      </Typography>
      <Typography level="body-xs">
        <strong>Now:</strong> {formatDeltaValue(fieldDelta.currentValue)}
      </Typography>
    </div>
  ) : null;

  // Compute beforeData by applying ALL deltas to reconstruct the original state.
  // For "create" nodes there is no before state, so use an empty object.
  const allDeltas = pendingChange?.deltas ?? [];
  const beforeData = useMemo(() => {
    if (!deltaMode) return fieldValues;
    if (changeVariant === "create") return {} as Record<string, unknown>;
    if (allDeltas.length === 0) return fieldValues;
    return createBeforeData(fieldValues, allDeltas, inputKeyToObjectKey);
  }, [deltaMode, changeVariant, allDeltas, fieldValues, inputKeyToObjectKey]);

  // Delta mode: render 4 columns (label, before, →, after)
  if (deltaMode) {
    return (
      <>
        <NodeLabelCell
          fieldDefinition={fieldDefinition}
          size={size}
          hasDelta={hasDelta}
          deltaMode={true}
          cellBackground={cellBackground}
          deltaTooltip={null}
          isRowDimmed={isRowDimmed}
        />

        {/* Before value cell - always read-only; empty for creates (no prior state) */}
        <NodeValueCell
          fieldDefinition={fieldDefinition}
          data={beforeData}
          store={store}
          isEditable={false}
          isLoading={isLoading}
          isMultiline={isMultiline}
          isSingleClick={false}
          size={size}
          cellStyles={cellStyles}
          variant="before"
          deltaMode={true}
          cellBackground={cellBackground}
          isLastColumn={false}
          hasDelta={hasDelta}
          isRowDimmed={isRowDimmed}
          changeVariant={changeVariant}
          fieldVariant={fieldVariant}
        />

        {/* Arrow separator — coloured when this row has a delta.
            ::before extends 1px left/right to cover the grid-gap border lines. */}
        <div
          className={cn(
            "flex items-center justify-center bg-(--cell-background) min-h-(--node-row-height) h-(--node-row-height) relative",
            size === "sm" ? "pl-1 pr-1 text-[11px]" : "pl-2 pr-2 text-[12px]",
            "before:content-[''] before:absolute before:top-0 before:bottom-0 before:-left-px before:right-0 before:bg-(--cell-background)",
          )}
          style={
            {
              "--cell-background": cellBackground,
              "--node-row-height": `${rowHeight}px`,
            } as CSSProperties
          }
        >
          <div
            data-dimmed-content={isRowDimmed ? true : undefined}
            className={cn(
              "relative",
              hasDelta
                ? changeVariant === "create"
                  ? "text-foreground"
                  : "text-foreground"
                : "text-muted-foreground/80",
              isRowDimmed && "opacity-[0.35] filter-[saturate(0)]",
            )}
          >
            →
          </div>
        </div>

        {/* After value cell - uses current data, respects field editability */}
        <NodeValueCell
          fieldDefinition={fieldDefinition}
          data={fieldValues}
          store={store}
          fieldEvents={fieldEvents}
          isEditable={isEditable}
          isLoading={isLoading}
          isMultiline={isMultiline}
          isSingleClick={effectiveSingleClick}
          size={size}
          cellProps={cellProps}
          cellStyles={cellStyles}
          variant={hasDelta ? "after" : undefined}
          deltaMode={true}
          cellBackground={cellBackground}
          isLastColumn={true}
          hasDelta={hasDelta}
          isRowDimmed={isRowDimmed}
          changeVariant={changeVariant}
          fieldVariant={fieldVariant}
        />
      </>
    );
  }

  // Standard 2-column layout (label + value)
  const compactCellStyle = deltaMode || isGridStyle;
  // A new record has no changed fields to point at — the card's own "Created"
  // status says what happened. Marking stays for updates, where it says which
  // fields actually moved.
  const marksChangedFields = hasDelta && changeVariant !== "create";
  return (
    <>
      <NodeLabelCell
        fieldDefinition={fieldDefinition}
        size={size}
        hasDelta={marksChangedFields}
        deltaMode={compactCellStyle}
        deltaTooltip={deltaTooltip}
        isRowDimmed={false}
        requiredState={
          !isCreating || !fieldDefinition.isRequired
            ? null
            : isMissingRequired
              ? "missing"
              : "filled"
        }
      />

      <NodeValueCell
        fieldDefinition={fieldDefinition}
        data={fieldValues}
        store={store}
        fieldEvents={fieldEvents}
        isEditable={isEditable}
        isLoading={isLoading}
        isMultiline={isMultiline}
        isSingleClick={effectiveSingleClick}
        size={size}
        cellProps={cellProps}
        cellStyles={{
          ...cellStyles,
          ...(marksChangedFields && {
            outline: "1px solid",
            outlineColor: "var(--foreground)",
          }),
          ...(isRevealingMissingRequired &&
            isMissingRequired && {
              outline: "1px solid",
              outlineColor: "var(--destructive)",
            }),
        }}
        cellClassName={marksChangedFields ? "bg-secondary" : undefined}
        deltaMode={compactCellStyle}
        fieldVariant={fieldVariant}
      />
    </>
  );
}
